// DR-017 Business Web MFA gate — TOTP enrolment / challenge / recovery.
// Security: the enrolment secret + otpauth URI + recovery codes live ONLY in component state, are
// cleared on completion/cancel/unmount, and are NEVER written to localStorage / URLs / logs. Errors are
// shown via friendly mfaMessage() only. Six-digit validation + double-submit guard. No MFA state logs out.
import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getFreshIdToken } from '../../../lib/firebase';
import { MFA_STATES, mfaApi, mfaMessage, isValidOtp } from '../../../lib/mfaClient';

// Group a base32 setup key into readable 4-char blocks for the manual fallback (display only).
const groupKey = (k) => (k || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

const getToken = () => getFreshIdToken();

export default function MfaGate({ initialState, onAssured, onCancel }) {
  const [phase, setPhase] = useState(initialState || null); // status | enroll | challenge | recovery
  const [enroll, setEnroll] = useState(null);   // { otpauth_uri, manual_key, challenge_id } — transient
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const mounted = useRef(true);

  // Clear ALL secret material on unmount (defence in depth) — otpauth URI, manual key, recovery codes.
  useEffect(() => () => { mounted.current = false; setEnroll(null); setRecoveryCodes(null); setCode(''); setCopied(false); }, []);

  async function copyKey() {
    try { await navigator.clipboard.writeText(enroll.manual_key); setCopied(true); setTimeout(() => mounted.current && setCopied(false), 2000); }
    catch { /* clipboard blocked — user can still read the grouped key */ }
  }

  useEffect(() => { if (!initialState) refreshStatus(); /* eslint-disable-next-line */ }, []);

  async function refreshStatus() {
    const r = await mfaApi.status(getToken);
    if (!mounted.current) return;
    const st = r.data && r.data.state;
    if (st === MFA_STATES.ASSURANCE_VALID || st === MFA_STATES.NOT_REQUIRED) { onAssured && onAssured(); return; }
    if (!r.data?.enrolled || st === 'MFA_EMAIL_ENROLMENT_REQUIRED' || st === 'MFA_ENROLMENT_REQUIRED') setPhase('enroll');
    else setPhase('challenge');
  }

  async function startEnrol() {
    if (busy) return; setBusy(true); setError('');
    const r = await mfaApi.enrollStart(getToken);
    setBusy(false);
    if (!mounted.current) return;
    if (!r.ok) { setError(r.data?.detail?.state ? mfaMessage(r.data.detail.state) : 'Could not start setup.'); return; }
    // transient secret material — component state only, never persisted
    setEnroll({ otpauth_uri: r.data.otpauth_uri, manual_key: r.data.manual_key, challenge_id: r.data.challenge_id });
  }

  async function submitEnrol() {
    if (busy || !isValidOtp(code)) { if (!isValidOtp(code)) setError('Enter the 6-digit code.'); return; }
    setBusy(true); setError('');
    const r = await mfaApi.enrollVerify(enroll.challenge_id, code.trim(), getToken);
    setBusy(false);
    if (!mounted.current) return;
    if (!r.ok) { setError(mfaMessage(r.state || MFA_STATES.INVALID_CODE)); setCode(''); return; }
    setEnroll(null); // drop the secret immediately on success
    if (r.data.recovery_codes) setRecoveryCodes(r.data.recovery_codes);
    else { onAssured && onAssured(); }
  }

  async function submitChallenge() {
    if (busy || !isValidOtp(code)) { if (!isValidOtp(code)) setError('Enter the 6-digit code.'); return; }
    setBusy(true); setError('');
    const r = await mfaApi.verify(code.trim(), 'login', getToken);
    setBusy(false);
    if (!mounted.current) return;
    if (!r.ok) { setError(mfaMessage(r.state || MFA_STATES.INVALID_CODE)); setCode(''); return; }
    onAssured && onAssured();
  }

  async function submitRecovery() {
    if (busy || !code.trim()) return;
    setBusy(true); setError('');
    const r = await mfaApi.recoveryVerify(code.trim(), getToken);
    setBusy(false);
    if (!mounted.current) return;
    if (!r.ok) { setError('Invalid or already-used recovery code.'); setCode(''); return; }
    onAssured && onAssured();
  }

  const box = { maxWidth: 420, margin: '0 auto', padding: 16, maxHeight: '90vh', overflowY: 'auto' };
  const input6 = (
    <input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      className="w-full mt-1 px-3 py-2 border rounded-md tracking-widest text-center font-mono"
      placeholder="123456" aria-label="6-digit code" />
  );

  if (recoveryCodes) {
    return (
      <div style={box}>
        <h3 className="text-lg font-semibold">Save your recovery codes</h3>
        <p className="text-sm text-gray-600">Store these somewhere safe. Each works once. They are shown only now.</p>
        <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
          {recoveryCodes.map((c) => <li key={c} className="bg-gray-50 rounded px-2 py-1">{c}</li>)}
        </ul>
        <button className="mt-4 w-full bg-blue-600 text-white rounded-md py-2"
          onClick={() => { setRecoveryCodes(null); onAssured && onAssured(); }}>I have saved them</button>
      </div>
    );
  }

  return (
    <div style={box}>
      {phase === 'enroll' && !enroll && (
        <>
          <h3 className="text-lg font-semibold">Set up two-factor authentication</h3>
          <p className="text-sm text-gray-600">Use an authenticator app (Google Authenticator, Microsoft Authenticator, Authy).</p>
          <button disabled={busy} className="mt-4 w-full bg-blue-600 text-white rounded-md py-2" onClick={startEnrol}>
            {busy ? 'Preparing…' : 'Begin setup'}</button>
        </>
      )}
      {phase === 'enroll' && enroll && (
        <>
          <h3 className="text-lg font-semibold">Add this account to your authenticator</h3>
          <p className="text-sm text-gray-600 mt-1">
            Scan this QR code using Google Authenticator, Microsoft Authenticator, Authy, 1Password, or another compatible authenticator app.
          </p>
          {/* PRIMARY: QR rendered LOCALLY from the exact server-provided otpauth_uri — no external QR service,
              no client-side URI reconstruction. */}
          {enroll.otpauth_uri && (
            <div className="mt-3 flex justify-center">
              <div className="bg-white p-3 rounded-md border" role="img" aria-label="TOTP authenticator enrolment QR code">
                <QRCodeSVG value={enroll.otpauth_uri} size={232} level="M" includeMargin marginSize={4} />
              </div>
            </div>
          )}
          {/* Same-device convenience: hand off to an installed authenticator via the otpauth URI. Never the
              only method; the URI is not typed into the address bar. */}
          {enroll.otpauth_uri && (
            <a href={enroll.otpauth_uri}
               className="mt-3 block w-full text-center text-sm text-blue-600 border border-blue-200 rounded-md py-2">
              Open authenticator app
            </a>
          )}

          <div className="flex items-center my-4">
            <div className="flex-1 border-t" /><span className="px-3 text-xs text-gray-400">OR</span><div className="flex-1 border-t" />
          </div>

          {/* FALLBACK: manual setup key, grouped + copyable. Preserved exactly as required. */}
          <label className="text-sm font-medium block">Enter this setup key manually</label>
          <code className="block mt-1 bg-gray-50 rounded px-3 py-2 break-all font-mono text-sm tracking-wider">{groupKey(enroll.manual_key)}</code>
          <button type="button" onClick={copyKey}
            className="mt-2 w-full text-sm border rounded-md py-2 text-gray-700">{copied ? 'Copied ✓' : 'Copy setup key'}</button>
          <p className="text-xs text-gray-500 mt-2">Issuer: Servisfy · 6 digits · 30 seconds</p>

          <label className="text-sm font-medium mt-4 block">Enter the current 6-digit code</label>
          {input6}
          <button disabled={busy} className="mt-3 w-full bg-blue-600 text-white rounded-md py-2" onClick={submitEnrol}>
            {busy ? 'Verifying…' : 'Verify & finish'}</button>
        </>
      )}
      {phase === 'challenge' && (
        <>
          <h3 className="text-lg font-semibold">Enter your authenticator code</h3>
          <p className="text-sm text-gray-600">{mfaMessage(MFA_STATES.CHALLENGE_REQUIRED)}</p>
          {input6}
          <button disabled={busy} className="mt-3 w-full bg-blue-600 text-white rounded-md py-2" onClick={submitChallenge}>
            {busy ? 'Verifying…' : 'Verify'}</button>
          <button className="mt-2 w-full text-sm text-gray-500" onClick={() => { setPhase('recovery'); setCode(''); setError(''); }}>
            Use a recovery code instead</button>
        </>
      )}
      {phase === 'recovery' && (
        <>
          <h3 className="text-lg font-semibold">Use a recovery code</h3>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX"
            className="w-full mt-2 px-3 py-2 border rounded-md font-mono" aria-label="recovery code" />
          <button disabled={busy} className="mt-3 w-full bg-blue-600 text-white rounded-md py-2" onClick={submitRecovery}>
            {busy ? 'Verifying…' : 'Verify'}</button>
          <button className="mt-2 w-full text-sm text-gray-500" onClick={() => { setPhase('challenge'); setCode(''); setError(''); }}>Back</button>
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
      {onCancel && <button className="mt-3 w-full text-sm text-gray-400" onClick={onCancel}>Cancel</button>}
    </div>
  );
}
